import { logger } from "../../utils/console";
import publicKey from "./publicKey";

const signingAlgorithm = "SHA-256";

/**
 * Verifies a license key using ECDSA and the provided public key.
 * @param license The license key string in the format <projectName>-<signature>
 * @returns Promise<boolean> indicating if the license is valid
 */
export async function verifyLicense(license: string): Promise<boolean> {
    if (typeof license !== "string" || !license.includes("-")) {
        logger.error("License Verification: License key is missing or malformed.");
        return false;
    }
    const { projectName, signature } = deconstructLicenseKey(license);
    if (!projectName || !signature) {
        logger.error("License Verification: License key is missing project name or signature.");
        return false;
    }

    const keyData = pemToArrayBuffer(publicKey);
    if (keyData === null) {
        logger.error("License Verification: Could not parse public key.");
        return false;
    }
    const cryptoKey = await crypto.subtle.importKey(
        "spki",
        keyData,
        {
            name: "ECDSA",
            namedCurve: "P-256"
        },
        false,
        ["verify"]
    );

    const signatureBuffer = base64urlToUint8Array(signature);
    if (signatureBuffer === null) {
        logger.error("License Verification: Could not parse signature.");
        return false;
    }
    const encoder = new TextEncoder();
    return crypto.subtle.verify(
        {
            name: "ECDSA",
            hash: { name: signingAlgorithm }
        },
        cryptoKey,
        signatureBuffer,
        encoder.encode(projectName)
    );
}

/**
 * Deconstructs a license key into projectName and signature.
 * @param licenseKey The license key string
 * @returns Object with projectName and signature
 */
function deconstructLicenseKey(licenseKey: string): {
    projectName: string;
    signature: string;
} {
    if (typeof licenseKey !== "string" || !licenseKey.includes("-")) {
        return { projectName: "", signature: "" };
    }
    const separatorIndex = licenseKey.indexOf("-");
    if (separatorIndex === -1 || separatorIndex === 0 || separatorIndex === licenseKey.length - 1) {
        return { projectName: "", signature: "" };
    }
    const projectName = licenseKey.substring(0, separatorIndex);
    const signature = licenseKey.substring(separatorIndex + 1);
    return { projectName, signature };
}

// Helper: Convert PEM to ArrayBuffer
function pemToArrayBuffer(pem: string): ArrayBuffer | null {
    const b64 = pem
        .replace(/-----BEGIN PUBLIC KEY-----/, "")
        .replace(/-----END PUBLIC KEY-----/, "")
        .replace(/\s/g, "");
    let binary;
    try {
        binary = atob(b64);
    } catch (e) {
        logger.debug(e);
        return null;
    }
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
}

// Helper: Decode base64url to Uint8Array
function base64urlToUint8Array(base64url: string): Uint8Array | null {
    // Pad base64url string and replace chars
    let base64 = base64url.replace(/-/g, "+").replace(/_/g, "/").replace(/\s/g, "");
    while (base64.length % 4) {
        base64 += "=";
    }
    try {
        return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    } catch (e) {
        logger.debug(e);
        return null;
    }
}
